import { useState, useEffect, useCallback } from 'react';
import { api } from '../../context/AuthContext';
import { API } from '../../config/api';
import { Search, Filter, ChevronLeft, ChevronRight, Receipt, X, Printer, Mail, Copy, Check, ExternalLink, Calendar, Loader2, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const TYPE_COLORS = {
  track: '#FF3B30', merch: '#DAA520', beat: '#8B5CF6',
  album: '#22C55E', donation: '#1DB954',
};
const STATUS_COLORS = {
  paid: '#22C55E', pending: '#FF6B35', failed: '#FF3B30', refunded: '#737373',
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  } catch { return iso; }
};
const fmtAmount = (v, cur = 'usd') =>
  `${(cur || 'usd').toUpperCase() === 'USD' ? '$' : ''}${Number(v || 0).toFixed(2)}`;

export function ReceiptsPanel({ openReceiptSession, onReceiptOpened }) {
  // View toggle: 'receipts' | 'customers'
  const [view, setView] = useState('receipts');
  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(0);
  const [summary, setSummary] = useState({ revenue: 0, paid_count: 0 });
  const [loading, setLoading] = useState(true);

  const [filters, setFilters] = useState({ q: '', status: '', orderType: '', start: '', end: '', page: 1 });
  const { q, status, orderType, start, end, page } = filters;
  const setPage = useCallback((p) => setFilters((f) => ({ ...f, page: typeof p === 'function' ? p(f.page) : p })), []);

  // Receipt modal — local state, or driven by parent's openReceiptSession prop
  const [localOpenSession, setLocalOpenSession] = useState(null);
  const openSession = localOpenSession || openReceiptSession || null;
  const closeReceipt = () => {
    setLocalOpenSession(null);
    if (onReceiptOpened) onReceiptOpened();
  };

  const limit = 25;

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), limit: String(limit) });
      if (q.trim()) params.set('q', q.trim());
      if (status) params.set('status', status);
      if (orderType) params.set('type', orderType);
      if (start) params.set('start', start);
      if (end) params.set('end', end);
      const { data } = await api.get(`${API}/admin/orders?${params.toString()}`);
      setOrders(data.orders || []);
      setTotal(data.total || 0);
      setPages(data.pages || 0);
      setSummary(data.summary || { revenue: 0, paid_count: 0 });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load receipts');
    } finally {
      setLoading(false);
    }
  }, [page, q, status, orderType, start, end]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);
  return (
    <div className="bg-[#141414] border border-[#262626] p-4 rounded-xl space-y-4" data-testid="receipts-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <h3 className="text-sm font-bold uppercase tracking-wider text-[#DAA520] flex items-center gap-2">
            <Receipt className="w-4 h-4" /> {view === 'receipts' ? 'All Receipts' : 'Customers'}
          </h3>
          <div className="flex bg-[#0A0A0A] border border-[#262626] rounded-md p-0.5">
            <button
              onClick={() => setView('receipts')}
              className={`px-2.5 py-1 text-[10px] uppercase tracking-wider font-bold rounded transition-colors ${view === 'receipts' ? 'bg-[#DAA520] text-black' : 'text-[#737373] hover:text-[#DAA520]'}`}
              data-testid="receipts-view-receipts"
            >Receipts</button>
            <button
              onClick={() => setView('customers')}
              className={`px-2.5 py-1 text-[10px] uppercase tracking-wider font-bold rounded transition-colors ${view === 'customers' ? 'bg-[#DAA520] text-black' : 'text-[#737373] hover:text-[#DAA520]'}`}
              data-testid="receipts-view-customers"
            >Customers</button>
          </div>
        </div>
        <div className="text-xs text-[#737373] font-mono">
          <span className="text-[#22C55E] font-bold">${summary.revenue.toFixed(2)}</span>
          <span className="mx-1.5">·</span>
          <span>{summary.paid_count} paid</span>
          <span className="mx-1.5">·</span>
          <span>{total} total</span>
        </div>
      </div>

      {view === 'customers' ? (
        <CustomersView onOpenCustomer={(email) => { setFilters({ q: email, status: '', orderType: '', start: '', end: '', page: 1 }); setView('receipts'); }} />
      ) : (
        <>
      {/* Filters */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-2">
        <div className="relative lg:col-span-2">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#525252]" />
          <input
            value={q}
            onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value, page: 1 }))}
            placeholder="Search email, item, session…"
            className="w-full bg-[#0A0A0A] border border-[#262626] rounded-md pl-8 pr-2 py-1.5 text-xs text-[#E5E5E5] placeholder-[#525252] focus:outline-none focus:border-[#DAA520]"
            data-testid="receipts-search"
          />
        </div>
        <select
          value={status}
          onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value, page: 1 }))}
          className="bg-[#0A0A0A] border border-[#262626] rounded-md px-2 py-1.5 text-xs text-[#E5E5E5] focus:outline-none focus:border-[#DAA520]"
          data-testid="receipts-status-filter"
        >
          <option value="">All statuses</option>
          <option value="paid">Paid</option>
          <option value="pending">Pending</option>
          <option value="failed">Failed</option>
          <option value="refunded">Refunded</option>
        </select>
        <select
          value={orderType}
          onChange={(e) => setFilters((f) => ({ ...f, orderType: e.target.value, page: 1 }))}
          className="bg-[#0A0A0A] border border-[#262626] rounded-md px-2 py-1.5 text-xs text-[#E5E5E5] focus:outline-none focus:border-[#DAA520]"
          data-testid="receipts-type-filter"
        >
          <option value="">All types</option>
          <option value="track">Tracks</option>
          <option value="merch">Merch</option>
          <option value="beat">Beats</option>
          <option value="album">Albums</option>
          <option value="donation">Donations</option>
        </select>
        <div className="relative">
          <Calendar className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#525252] pointer-events-none" />
          <input
            type="date"
            value={start}
            onChange={(e) => setFilters((f) => ({ ...f, start: e.target.value, page: 1 }))}
            className="w-full bg-[#0A0A0A] border border-[#262626] rounded-md pl-8 pr-2 py-1.5 text-xs text-[#E5E5E5] focus:outline-none focus:border-[#DAA520]"
            data-testid="receipts-start-date"
            title="From date"
          />
        </div>
        <div className="relative">
          <Calendar className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#525252] pointer-events-none" />
          <input
            type="date"
            value={end}
            onChange={(e) => setFilters((f) => ({ ...f, end: e.target.value, page: 1 }))}
            className="w-full bg-[#0A0A0A] border border-[#262626] rounded-md pl-8 pr-2 py-1.5 text-xs text-[#E5E5E5] focus:outline-none focus:border-[#DAA520]"
            data-testid="receipts-end-date"
            title="To date"
          />
        </div>
      </div>

      {(q || status || orderType || start || end) && (
        <div className="flex items-center justify-between flex-wrap gap-2">
          <button
            onClick={() => setFilters({ q: '', status: '', orderType: '', start: '', end: '', page: 1 })}
            className="text-[10px] uppercase tracking-wider text-[#737373] hover:text-[#DAA520] flex items-center gap-1"
            data-testid="receipts-reset-filters"
          >
            <Filter className="w-3 h-3" /> Clear filters
          </button>
          {(status === 'pending' || status === 'failed') && total > 0 && (
            <button
              onClick={async () => {
                if (!window.confirm(`Delete ${total} ${status} order(s)? Cannot be undone.`)) return;
                try {
                  const { data } = await api.post(`${API}/admin/orders/bulk-delete`, { filter: { status } });
                  toast.success(`Deleted ${data.deleted} row(s)`);
                  setFilters((f) => ({ ...f, page: 1 }));
                  // Refetch by toggling page
                  setTimeout(() => setFilters((f) => ({ ...f })), 100);
                } catch (err) {
                  toast.error(err?.response?.data?.detail || 'Bulk delete failed');
                }
              }}
              className="text-[10px] uppercase tracking-wider text-[#FF3B30] hover:text-white flex items-center gap-1"
              data-testid="receipts-bulk-delete"
            >
              <Trash2 className="w-3 h-3" /> Delete all {status}
            </button>
          )}
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className="py-12 text-center text-[#737373] flex items-center justify-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading receipts…
        </div>
      ) : orders.length === 0 ? (
        <div className="py-12 text-center text-[#737373] text-sm" data-testid="receipts-empty">
          No receipts match these filters.
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-[10px] uppercase text-[#737373] border-b border-[#262626]">
                  <th className="py-2 px-2 font-normal">Date</th>
                  <th className="py-2 px-2 font-normal">Item</th>
                  <th className="py-2 px-2 font-normal hidden md:table-cell">Customer</th>
                  <th className="py-2 px-2 font-normal">Type</th>
                  <th className="py-2 px-2 font-normal text-right">Amount</th>
                  <th className="py-2 px-2 font-normal text-right">Status</th>
                  <th className="py-2 px-2 font-normal w-8" />
                </tr>
              </thead>
              <tbody>
                {orders.map((o) => (
                  <tr
                    key={o.id || o.session_id}
                    onClick={() => setLocalOpenSession(o.session_id)}
                    className="border-b border-[#1a1a1a] hover:bg-[#1a1a1a] cursor-pointer transition-colors"
                    data-testid={`receipt-row-${o.session_id}`}
                  >
                    <td className="py-2 px-2 text-[#A3A3A3] whitespace-nowrap">
                      {fmtDate(o.completed_at || o.created_at)}
                    </td>
                    <td className="py-2 px-2 text-[#E5E5E5] truncate max-w-[200px]">{o.item_title}</td>
                    <td className="py-2 px-2 text-[#737373] hidden md:table-cell truncate max-w-[180px]">
                      {o.customer_email || <span className="text-[#525252]">—</span>}
                    </td>
                    <td className="py-2 px-2">
                      <span className="inline-flex items-center gap-1.5">
                        <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: TYPE_COLORS[o.type] || '#666' }} />
                        <span className="uppercase text-[10px] text-[#A3A3A3]">{o.type}</span>
                      </span>
                    </td>
                    <td className="py-2 px-2 font-mono font-bold text-right">${o.amount.toFixed(2)}</td>
                    <td className="py-2 px-2 text-right">
                      <span
                        className="uppercase text-[9px] font-bold px-1.5 py-0.5 rounded"
                        style={{
                          color: STATUS_COLORS[o.payment_status] || '#666',
                          backgroundColor: `${STATUS_COLORS[o.payment_status] || '#666'}20`,
                        }}
                      >
                        {o.payment_status}
                      </span>
                    </td>
                    <td className="py-2 px-2 text-right">
                      <Receipt className="w-3.5 h-3.5 text-[#525252] inline-block" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pages > 1 && (
            <div className="flex items-center justify-between text-xs text-[#737373]" data-testid="receipts-pagination">
              <span>
                Page {page} of {pages} · showing {orders.length} of {total}
              </span>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page <= 1}
                  className="p-1.5 rounded border border-[#262626] hover:border-[#DAA520] disabled:opacity-30 disabled:hover:border-[#262626]"
                  data-testid="receipts-prev"
                ><ChevronLeft className="w-3.5 h-3.5" /></button>
                <button
                  onClick={() => setPage(Math.min(pages, page + 1))}
                  disabled={page >= pages}
                  className="p-1.5 rounded border border-[#262626] hover:border-[#DAA520] disabled:opacity-30 disabled:hover:border-[#262626]"
                  data-testid="receipts-next"
                ><ChevronRight className="w-3.5 h-3.5" /></button>
              </div>
            </div>
          )}
        </>
      )}
        </>
      )}

      {openSession && (
        <ReceiptModal sessionId={openSession} onClose={closeReceipt} />
      )}
    </div>
  );
}


function ReceiptModal({ sessionId, onClose }) {
  const [receipt, setReceipt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resending, setResending] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`${API}/admin/orders/${sessionId}/receipt`);
        if (alive) setReceipt(data);
      } catch (err) {
        toast.error(err?.response?.data?.detail || 'Failed to load receipt');
        if (alive) onClose();
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [sessionId, onClose]);

  const handlePrint = () => window.print();

  const handleResend = async () => {
    if (!receipt?.customer_email) {
      toast.error('No customer email on file');
      return;
    }
    setResending(true);
    try {
      const { data } = await api.post(`${API}/admin/orders/${sessionId}/resend-receipt`);
      toast.success(`Receipt re-sent to ${data.sent_to}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to send receipt');
    } finally {
      setResending(false);
    }
  };

  const copySession = () => {
    navigator.clipboard.writeText(sessionId);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div
      className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[200] flex items-center justify-center p-4 print:bg-white print:p-0"
      onClick={onClose}
      data-testid="receipt-modal"
    >
      <div
        className="bg-[#0A0A0A] border-2 border-[#DAA520]/40 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto print:max-h-none print:bg-white print:text-black print:border-0"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 z-10 bg-[#0A0A0A] border-b border-[#262626] p-5 flex items-center justify-between print:hidden">
          <div>
            <h2 className="font-bold text-lg text-[#DAA520] uppercase tracking-wide flex items-center gap-2">
              <Receipt className="w-5 h-5" /> Receipt
            </h2>
            <button
              onClick={copySession}
              className="text-[10px] text-[#525252] font-mono mt-0.5 flex items-center gap-1 hover:text-[#DAA520]"
              data-testid="receipt-copy-session"
            >
              {sessionId.slice(0, 30)}…
              {copied ? <Check className="w-3 h-3 text-[#22C55E]" /> : <Copy className="w-3 h-3" />}
            </button>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={handlePrint}
              className="p-2 hover:bg-[#1a1a1a] rounded transition-colors"
              title="Print receipt"
              data-testid="receipt-print"
            ><Printer className="w-4 h-4 text-[#A3A3A3]" /></button>
            {receipt?.payment_status === 'paid' && receipt?.customer_email && (
              <button
                onClick={handleResend}
                disabled={resending}
                className="p-2 hover:bg-[#1a1a1a] rounded transition-colors disabled:opacity-50"
                title="Re-email this receipt"
                data-testid="receipt-resend"
              >
                {resending ? <Loader2 className="w-4 h-4 text-[#A3A3A3] animate-spin" /> : <Mail className="w-4 h-4 text-[#A3A3A3]" />}
              </button>
            )}
            <button
              onClick={onClose}
              className="p-2 hover:bg-[#1a1a1a] rounded transition-colors"
              data-testid="receipt-close"
            ><X className="w-4 h-4 text-[#A3A3A3]" /></button>
          </div>
        </div>

        {/* Body */}
        {loading ? (
          <div className="p-12 text-center text-[#737373] flex items-center justify-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading receipt…
          </div>
        ) : receipt ? (
          <div className="p-5 space-y-4 print:p-8">
            {/* Status banner */}
            <div
              className="flex items-center justify-between p-3 rounded-lg border print:border-black"
              style={{
                borderColor: STATUS_COLORS[receipt.payment_status] || '#666',
                backgroundColor: `${STATUS_COLORS[receipt.payment_status] || '#666'}15`,
              }}
            >
              <span
                className="uppercase text-xs font-bold tracking-wider"
                style={{ color: STATUS_COLORS[receipt.payment_status] || '#666' }}
              >
                {receipt.payment_status}
              </span>
              <span className="font-mono font-bold text-2xl" style={{ color: STATUS_COLORS[receipt.payment_status] || '#666' }}>
                {fmtAmount(receipt.amount, receipt.currency)}
              </span>
            </div>

            {/* Customer */}
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div>
                <p className="text-[10px] uppercase text-[#737373] print:text-gray-500">Customer</p>
                <p className="text-[#E5E5E5] print:text-black font-mono">
                  {receipt.customer_email || <span className="text-[#525252]">No email on file</span>}
                </p>
                {receipt.customer_name && (
                  <p className="text-[#A3A3A3] print:text-gray-700 text-[11px]">{receipt.customer_name}</p>
                )}
              </div>
              <div>
                <p className="text-[10px] uppercase text-[#737373] print:text-gray-500">Date</p>
                <p className="text-[#E5E5E5] print:text-black">{fmtDate(receipt.completed_at || receipt.created_at)}</p>
              </div>
            </div>

            {/* Line items */}
            <div className="space-y-2">
              <p className="text-[10px] uppercase text-[#737373] print:text-gray-500">Items</p>
              {receipt.line_items.map((li, i) => (
                <div
                  key={li.transaction_id || i}
                  className="flex items-center gap-3 bg-[#141414] border border-[#262626] p-3 rounded-lg print:bg-transparent print:border-gray-300"
                  data-testid={`receipt-item-${i}`}
                >
                  {li.cover ? (
                    <img src={li.cover} alt="" className="w-12 h-12 object-cover rounded shrink-0" />
                  ) : (
                    <div
                      className="w-12 h-12 rounded shrink-0 flex items-center justify-center"
                      style={{ backgroundColor: `${TYPE_COLORS[li.type] || '#666'}30` }}
                    >
                      <Receipt className="w-5 h-5" style={{ color: TYPE_COLORS[li.type] || '#666' }} />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-[#E5E5E5] print:text-black truncate">{li.title}</p>
                    {li.subtitle && <p className="text-[10px] text-[#A3A3A3] print:text-gray-600 truncate">{li.subtitle}</p>}
                    <p className="text-[10px] uppercase text-[#525252] mt-0.5">{li.type}</p>
                    {li.discount_code && (
                      <p className="text-[10px] text-[#DAA520] mt-0.5">
                        Discount: {li.discount_code} (−${(li.discount_amount || 0).toFixed(2)})
                      </p>
                    )}
                    {li.message && <p className="text-[10px] text-[#A3A3A3] mt-1 italic">“{li.message}”</p>}
                  </div>
                  <p className="font-mono font-bold text-[#E5E5E5] print:text-black shrink-0">{fmtAmount(li.amount, receipt.currency)}</p>
                </div>
              ))}
            </div>

            {/* Total */}
            <div className="border-t border-[#262626] print:border-black pt-3 flex justify-between items-center">
              <span className="uppercase text-xs text-[#737373] tracking-wider">Total</span>
              <span className="font-mono font-bold text-xl text-[#22C55E] print:text-black">
                {fmtAmount(receipt.amount, receipt.currency)}
              </span>
            </div>

            {/* Download links */}
            {receipt.download_links && receipt.download_links.length > 0 && (
              <div className="space-y-2 print:hidden">
                <p className="text-[10px] uppercase text-[#737373]">Download links</p>
                <div className="space-y-1">
                  {receipt.download_links.map((dl) => (
                    <a
                      key={dl.token}
                      href={dl.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`flex items-center justify-between gap-2 bg-[#141414] border border-[#262626] hover:border-[#DAA520] p-2 rounded text-xs transition-colors ${dl.expired ? 'opacity-50' : ''}`}
                      data-testid={`receipt-download-${dl.token.slice(0,8)}`}
                    >
                      <span className="flex items-center gap-2 truncate">
                        <ExternalLink className="w-3 h-3 text-[#DAA520] shrink-0" />
                        <span className="font-mono text-[#A3A3A3] truncate">{dl.token.slice(0, 32)}…</span>
                      </span>
                      <span className="text-[9px] text-[#525252] uppercase shrink-0">
                        {dl.expired ? 'expired' : `exp ${new Date(dl.expires_at).toLocaleDateString()}`}
                      </span>
                    </a>
                  ))}
                </div>
              </div>
            )}

            {/* Session ID footer (visible on print) */}
            <p className="text-[9px] text-[#525252] print:text-gray-500 font-mono pt-2 break-all">{receipt.session_id}</p>
          </div>
        ) : null}
      </div>
    </div>
  );
}



function CustomersView({ onOpenCustomer }) {
  const [customers, setCustomers] = useState([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState({ total_revenue: 0, customer_count: 0 });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');

  useEffect(() => {
    let alive = true;
    const id = setTimeout(async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({ limit: '50' });
        if (q.trim()) params.set('q', q.trim());
        const { data } = await api.get(`${API}/admin/customers?${params.toString()}`);
        if (alive) {
          setCustomers(data.customers || []);
          setTotal(data.total || 0);
          setSummary(data.summary || { total_revenue: 0, customer_count: 0 });
        }
      } catch (err) {
        toast.error(err?.response?.data?.detail || 'Failed to load customers');
      } finally {
        if (alive) setLoading(false);
      }
    }, q ? 300 : 0); // debounce search
    return () => { alive = false; clearTimeout(id); };
  }, [q]);

  return (
    <div className="space-y-3" data-testid="customers-view">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#525252]" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search customer email…"
            className="w-full bg-[#0A0A0A] border border-[#262626] rounded-md pl-8 pr-2 py-1.5 text-xs text-[#E5E5E5] placeholder-[#525252] focus:outline-none focus:border-[#DAA520]"
            data-testid="customers-search"
          />
        </div>
        <p className="text-xs text-[#737373] font-mono">
          {summary.customer_count} customer{summary.customer_count !== 1 ? 's' : ''} · ${summary.total_revenue.toFixed(2)} LTV
        </p>
      </div>

      {loading ? (
        <div className="py-12 text-center text-[#737373] flex items-center justify-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading customers…
        </div>
      ) : customers.length === 0 ? (
        <div className="py-12 text-center text-[#737373] text-sm" data-testid="customers-empty">
          {q ? 'No customers match this search.' : 'No customers yet — paid orders missing email addresses won\'t appear here.'}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-[10px] uppercase text-[#737373] border-b border-[#262626]">
                <th className="py-2 px-2 font-normal">Customer</th>
                <th className="py-2 px-2 font-normal hidden md:table-cell">First / Last</th>
                <th className="py-2 px-2 font-normal text-center">Orders</th>
                <th className="py-2 px-2 font-normal hidden sm:table-cell">Bought</th>
                <th className="py-2 px-2 font-normal text-right">Lifetime</th>
              </tr>
            </thead>
            <tbody>
              {customers.map((c) => (
                <tr
                  key={c.email}
                  onClick={() => onOpenCustomer(c.email)}
                  className="border-b border-[#1a1a1a] hover:bg-[#1a1a1a] cursor-pointer transition-colors"
                  data-testid={`customer-row-${c.email}`}
                >
                  <td className="py-2 px-2 text-[#E5E5E5] truncate max-w-[240px] font-mono">{c.email}</td>
                  <td className="py-2 px-2 text-[#737373] hidden md:table-cell text-[10px]">
                    {c.first_purchase && new Date(c.first_purchase).toLocaleDateString()}
                    {c.last_purchase && c.last_purchase !== c.first_purchase && (
                      <> → {new Date(c.last_purchase).toLocaleDateString()}</>
                    )}
                  </td>
                  <td className="py-2 px-2 text-center font-mono font-bold">{c.order_count}</td>
                  <td className="py-2 px-2 hidden sm:table-cell">
                    <div className="flex flex-wrap gap-1">
                      {c.types.slice(0, 4).map((t) => (
                        <span key={t} className="text-[9px] uppercase px-1.5 py-0.5 rounded"
                          style={{
                            color: TYPE_COLORS[t] || '#666',
                            backgroundColor: `${TYPE_COLORS[t] || '#666'}20`,
                          }}
                        >{t}</span>
                      ))}
                    </div>
                  </td>
                  <td className="py-2 px-2 font-mono font-bold text-[#22C55E] text-right">
                    ${c.total_spent.toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {total > customers.length && (
            <p className="text-[10px] text-[#525252] mt-2 text-center">
              Showing top {customers.length} of {total} customers. Refine with search to see more.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
